import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/page-header';
import SettingsLayout from '@/components/settings-layout';
import { setWebTitle } from '@/lib/format-utils';
import {
    Stack,
    Group,
    Button,
    NumberInput,
    Select,
    ActionIcon,
    Switch
} from '@mantine/core';
import {
    IconPlus,
    IconTrash,
    IconPencil
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { ExchangeRate, Tenant } from '@turizi/shared';
import {
    useExchangeRates,
    useCreateExchangeRate,
    useDeleteExchangeRate,
    useUpdateExchangeRate,
    useUpdateTenant
} from '@/hooks/api-hooks';
import { localTenant, updateMe } from '@/services/session-service';
import { formatExchangeRate } from '@/lib/format-utils';
import { SkeletonList } from '@/components/snippets';
import CardModal from '@/components/card-modal';
import { EXCHANGE_RATE_MAX_DIGITS } from '@turizi/shared';

const EMPTY_FORM: Partial<ExchangeRate> = {
    from_currency: '',
    to_currency: '',
    rate: null,
};

export default function SettingsExchangeRatesPage(): JSX.Element {
    const { t, i18n } = useTranslation();
    const [modalOpened, setModalOpened] = useState(false);
    const [editingRate, setEditingRate] = useState<ExchangeRate | null>(null);
    const [rateForm, setRateForm] = useState(EMPTY_FORM);

    const tenant = localTenant();
    const updateTenantMutation = useUpdateTenant();
    const [draftTenant, setDraftTenant] = useState<Tenant | null>(tenant);

    setWebTitle(t('exchange_rates'));

    const {
        data: exchangeRatesData,
        isLoading: isLoadingRates
    } = useExchangeRates();
    const createExchangeRateMutation = useCreateExchangeRate();
    const updateExchangeRateMutation = useUpdateExchangeRate();
    const deleteExchangeRateMutation = useDeleteExchangeRate();

    const handleTogglePublicExchange = (checked: boolean) => {
        if (!tenant) return;
        updateTenantMutation.mutate({
            id: tenant.id,
            settings: {
                enable_public_exchange_rates: checked
            }
        }, {
            onSuccess: async () => {
                await updateMe(true);
                setDraftTenant(prev => prev ? {
                    ...prev,
                    settings: {
                        ...prev.settings,
                        enable_public_exchange_rates: checked
                    }
                } : null);
                notifications.show({
                    title: t('success'),
                    message: t('settings_saved_success'),
                    color: 'green',
                });
            },
            onError: (error: any) => {
                notifications.show({
                    title: t('error'),
                    message: error?.message || t('error'),
                    color: 'red',
                });
            }
        });
    };

    const exchangeRates = exchangeRatesData?.data || [];

    const currencies = Object.keys((i18n.t('currencies', { returnObjects: true }) as any) || {});

    const getCurrencyLabel = (code: string) => {
        return i18n.exists(`currencies.${code}`)
            ? t(`currencies.${code}`)
            : code;
    };

    const handleOpenModal = (rate?: ExchangeRate) => {
        if (rate) {
            setEditingRate(rate);
            setRateForm(rate);
        } else {
            setEditingRate(null);
            setRateForm(EMPTY_FORM);
        }
        setModalOpened(true);
    };

    const handleCloseModal = () => {
        setModalOpened(false);
        setEditingRate(null);
        setRateForm(EMPTY_FORM);
    };

    const handleSaveRate = () => {
        const mutation = editingRate ? updateExchangeRateMutation : createExchangeRateMutation;

        mutation.mutate(rateForm, {
            onSuccess: () => {
                handleCloseModal();
            },
            onError: (error: any) => {
                notifications.show({
                    title: t('error'),
                    message: error?.message || t('error'),
                    color: 'red',
                });
            }
        });
    };

    const handleDeleteRate = (id: number) => {
        if (confirm(t('confirm_delete'))) {
            deleteExchangeRateMutation.mutate(id);
        }
    };

    return (
        <div>
            <PageHeader title={t('settings')} />

            <div className="panel-content max-w-screen-lg">
                <SettingsLayout>
                    <div className='flex justify-between items-center mb-6'>
                        <div>
                            <div className='font-semibold text-gray-800 text-2xl mb-2'>
                                {t('exchange_rates')}
                            </div>
                            <div className='text-gray-700 text-sm mb-6'>
                                {t('exchange_rates_description')}
                            </div>
                        </div>
                        <Button
                            onClick={() => handleOpenModal()}
                            size="sm"
                            color='black'
                            radius="sm"
                        >
                            {t('add_exchange_rate')}
                        </Button>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 flex items-center justify-between">
                        <div className="flex-1 pr-4">
                            <div className="font-semibold text-gray-800 text-sm mb-1">
                                {t('enable_public_exchange_rates_title')}
                            </div>
                            <div className="text-gray-500 text-xs">
                                {t('enable_public_exchange_rates_desc')}
                            </div>
                        </div>
                        <Switch
                            checked={draftTenant?.settings?.enable_public_exchange_rates ?? false}
                            onChange={(event) => handleTogglePublicExchange(event.currentTarget.checked)}
                            disabled={updateTenantMutation.isPending}
                        />
                    </div>

                    <div className='solid-box-border'>
                        <div className='subtle-table'>
                            <table>
                                <thead>
                                    <tr>
                                        <th>{t('from_currency')}</th>
                                        <th>{t('to_currency')}</th>
                                        <th>{t('rate')}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {exchangeRates.map((rate) => (
                                        <tr key={rate.id}>
                                            <td>{getCurrencyLabel(rate.from_currency)}</td>
                                            <td>{getCurrencyLabel(rate.to_currency)}</td>
                                            <td>{formatExchangeRate(rate.rate)}</td>
                                            <td>
                                                <Group gap="xs" justify="flex-end">
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="gray"
                                                        onClick={() => handleOpenModal(rate)}
                                                    >
                                                        <IconPencil size={16} />
                                                    </ActionIcon>
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="red"
                                                        loading={deleteExchangeRateMutation.isPending}
                                                        onClick={() => handleDeleteRate(rate.id)}
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Group>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {isLoadingRates && <SkeletonList />}
                    </div>

                    {/* Exchange Rate Modal */}
                    <CardModal
                        opened={modalOpened}
                        onClose={handleCloseModal}
                        header={editingRate ? t('edit') + ' ' + t('exchange_rate') : t('add') + ' ' + t('exchange_rate')}
                    >
                        <Stack gap="md">
                            <Select
                                label={t('from_currency')}
                                placeholder={t('select')}
                                data={currencies.map(code => ({
                                    value: code,
                                    label: getCurrencyLabel(code)
                                }))}
                                value={rateForm.from_currency}
                                onChange={(value) => setRateForm({ ...rateForm, from_currency: value })}
                                searchable
                                required
                            />
                            <Select
                                label={t('to_currency')}
                                placeholder={t('select')}
                                data={currencies.map(code => ({
                                    value: code,
                                    label: getCurrencyLabel(code)
                                }))}
                                value={rateForm.to_currency}
                                onChange={(value) => setRateForm({ ...rateForm, to_currency: value })}
                                searchable
                                required
                            />
                            <NumberInput
                                label={t('rate')}
                                placeholder="1.00"
                                value={rateForm.rate}
                                onChange={(value) => setRateForm({ ...rateForm, rate: Number(value) })}
                                decimalScale={EXCHANGE_RATE_MAX_DIGITS}
                                required
                                thousandSeparator="."
                                decimalSeparator=","
                                hideControls
                            />
                            <Group justify="flex-end" gap="xs">
                                <Button
                                    variant="subtle"
                                    size='xs'
                                    color='black'
                                    onClick={handleCloseModal}>
                                    {t('cancel')}
                                </Button>
                                <Button
                                    size='xs'
                                    color='black'
                                    onClick={handleSaveRate}
                                    loading={createExchangeRateMutation.isPending || updateExchangeRateMutation.isPending}
                                >
                                    {t('save')}
                                </Button>
                            </Group>
                        </Stack>
                    </CardModal>
                </SettingsLayout>
            </div>
        </div>
    );
}
